export interface Point {
  x: number;
  y: number;
}

export interface NodeBounds extends Point {
  width: number;
  height: number;
}

export interface NodeSize {
  width: number;
  height: number;
}

const defaultNodeSize: NodeSize = { width: 360, height: 240 };
const insertionGap = 48;
const maximumCandidates = 48;

function intersects(left: NodeBounds, right: NodeBounds): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}

function isWithin(candidate: NodeBounds, bounds: NodeBounds): boolean {
  return candidate.x >= bounds.x &&
    candidate.y >= bounds.y &&
    candidate.x + candidate.width <= bounds.x + bounds.width &&
    candidate.y + candidate.height <= bounds.y + bounds.height;
}

function candidateAt(base: Point, size: NodeSize, index: number): NodeBounds {
  const directions = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: -1, y: 0 },
    { x: 0, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
    { x: -1, y: -1 },
    { x: 1, y: -1 }
  ];
  const ring = index < directions.length ? 1 : Math.floor((index - 1) / 8) + 1;
  const direction = index < directions.length
    ? directions[index]!
    : directions[1 + ((index - 1) % 8)]!;
  return {
    x: base.x + direction.x * ring * (size.width + insertionGap),
    y: base.y + direction.y * ring * (size.height + insertionGap),
    width: size.width,
    height: size.height
  };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function visibleCandidateXCoordinates(
  base: Point,
  size: NodeSize,
  visibleBounds: NodeBounds,
  occupiedBounds: readonly NodeBounds[]
): number[] {
  const minimum = visibleBounds.x;
  const maximum = visibleBounds.x + visibleBounds.width - size.width;
  if (maximum < minimum) return [];
  const xCoordinates = new Set<number>([minimum, maximum, clamp(base.x, minimum, maximum)]);
  for (const occupied of occupiedBounds) {
    xCoordinates.add(occupied.x - size.width);
    xCoordinates.add(occupied.x + occupied.width);
  }
  return Array.from(xCoordinates)
    .filter((x) => x >= minimum && x <= maximum)
    .sort((left, right) => left - right);
}

function lowerBound(values: readonly number[], target: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (values[middle]! < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

function upperBound(values: readonly number[], target: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (values[middle]! <= target) low = middle + 1;
    else high = middle;
  }
  return low;
}

class CoverageTree {
  private readonly minimum: number[];
  private readonly lazy: number[];

  constructor(private readonly length: number) {
    this.minimum = Array.from({ length: Math.max(1, length * 4) }, () => 0);
    this.lazy = Array.from({ length: Math.max(1, length * 4) }, () => 0);
  }

  add(start: number, end: number, delta: number): void {
    if (start > end || this.length === 0) return;
    this.addRange(1, 0, this.length - 1, start, end, delta);
  }

  findLastZero(maximumIndex: number): number | undefined {
    return this.findZero(1, 0, this.length - 1, 0, maximumIndex, true);
  }

  findFirstZero(minimumIndex: number): number | undefined {
    return this.findZero(1, 0, this.length - 1, minimumIndex, this.length - 1, false);
  }

  private addRange(
    node: number,
    left: number,
    right: number,
    start: number,
    end: number,
    delta: number
  ): void {
    if (start <= left && right <= end) {
      this.minimum[node] = this.minimum[node]! + delta;
      this.lazy[node] = this.lazy[node]! + delta;
      return;
    }
    this.push(node);
    const middle = Math.floor((left + right) / 2);
    if (start <= middle) this.addRange(node * 2, left, middle, start, end, delta);
    if (end > middle) this.addRange(node * 2 + 1, middle + 1, right, start, end, delta);
    this.minimum[node] = Math.min(this.minimum[node * 2]!, this.minimum[node * 2 + 1]!);
  }

  private findZero(
    node: number,
    left: number,
    right: number,
    start: number,
    end: number,
    reverse: boolean
  ): number | undefined {
    if (end < left || right < start || this.minimum[node]! > 0) return undefined;
    if (left === right) return left;
    this.push(node);
    const middle = Math.floor((left + right) / 2);
    const first = reverse
      ? this.findZero(node * 2 + 1, middle + 1, right, start, end, reverse)
      : this.findZero(node * 2, left, middle, start, end, reverse);
    if (first !== undefined) return first;
    return reverse
      ? this.findZero(node * 2, left, middle, start, end, reverse)
      : this.findZero(node * 2 + 1, middle + 1, right, start, end, reverse);
  }

  private push(node: number): void {
    const delta = this.lazy[node]!;
    if (delta === 0) return;
    for (const child of [node * 2, node * 2 + 1]) {
      this.minimum[child] = this.minimum[child]! + delta;
      this.lazy[child] = this.lazy[child]! + delta;
    }
    this.lazy[node] = 0;
  }
}

interface VerticalCoverageRange {
  start: number;
  end: number;
}

function closestVisibleCandidate(
  base: Point,
  size: NodeSize,
  visibleBounds: NodeBounds,
  occupiedBounds: readonly NodeBounds[],
  xCoordinates: readonly number[]
): Point | undefined {
  const minimumX = visibleBounds.x;
  const maximumX = visibleBounds.x + visibleBounds.width - size.width;
  const minimumY = visibleBounds.y;
  const maximumY = visibleBounds.y + visibleBounds.height - size.height;
  if (maximumX < minimumX || maximumY < minimumY) return undefined;

  const preferredY = clamp(base.y, minimumY, maximumY);
  const yCoordinates = new Set<number>([minimumY, maximumY, preferredY]);
  for (const occupied of occupiedBounds) {
    const start = occupied.y - size.height;
    const end = occupied.y + occupied.height;
    if (start >= minimumY && start <= maximumY) yCoordinates.add(start);
    if (end >= minimumY && end <= maximumY) yCoordinates.add(end);
  }
  const sortedY = Array.from(yCoordinates).sort((left, right) => left - right);
  const coverage = new CoverageTree(sortedY.length);
  const additions = new Map<number, VerticalCoverageRange[]>();
  const removals = new Map<number, VerticalCoverageRange[]>();

  const addEvent = (
    events: Map<number, VerticalCoverageRange[]>,
    x: number,
    range: VerticalCoverageRange
  ) => {
    const existing = events.get(x);
    if (existing) existing.push(range);
    else events.set(x, [range]);
  };
  const applyRange = ({ start, end }: VerticalCoverageRange, delta: number) => {
    const first = upperBound(sortedY, start);
    const last = lowerBound(sortedY, end) - 1;
    coverage.add(first, last, delta);
  };

  for (const occupied of occupiedBounds) {
    const startX = occupied.x - size.width;
    const endX = occupied.x + occupied.width;
    const range = { start: occupied.y - size.height, end: occupied.y + occupied.height };
    if (startX < minimumX && endX > minimumX) applyRange(range, 1);
    if (startX >= minimumX && startX <= maximumX) addEvent(additions, startX, range);
    if (endX > minimumX && endX <= maximumX) addEvent(removals, endX, range);
  }

  const preferredIndex = lowerBound(sortedY, preferredY);
  let best: Point | undefined;
  for (const x of xCoordinates) {
    // Horizontal overlap is open at both rectangle edges: remove endings before
    // evaluating x, and add beginnings afterwards, so touching remains legal.
    for (const range of removals.get(x) ?? []) applyRange(range, -1);
    const belowIndex = coverage.findLastZero(preferredIndex);
    const aboveIndex = coverage.findFirstZero(preferredIndex);
    const y = [belowIndex, aboveIndex]
      .filter((index): index is number => index !== undefined)
      .map((index) => sortedY[index]!)
      .sort((left, right) => Math.abs(left - base.y) - Math.abs(right - base.y) || left - right)[0];
    if (y === undefined) {
      for (const range of additions.get(x) ?? []) applyRange(range, 1);
      continue;
    }
    const candidate = { x, y };
    if (!best) {
      best = candidate;
    } else {
      const candidateDistance = Math.abs(candidate.x - base.x) + Math.abs(candidate.y - base.y);
      const bestDistance = Math.abs(best.x - base.x) + Math.abs(best.y - base.y);
      if (
        candidateDistance < bestDistance ||
        (candidateDistance === bestDistance && (candidate.y < best.y || (candidate.y === best.y && candidate.x < best.x)))
      ) {
        best = candidate;
      }
    }
    for (const range of additions.get(x) ?? []) applyRange(range, 1);
  }
  return best;
}

export function resolveInsertionPoint({
  explicitCanvasPoint,
  lastCanvasPoint,
  viewportCenter,
  occupiedNodeBounds = [],
  visibleNodeBounds,
  nodeSize = defaultNodeSize
}: {
  explicitCanvasPoint?: Point;
  lastCanvasPoint: Point | undefined;
  viewportCenter: Point | undefined;
  occupiedNodeBounds?: readonly NodeBounds[];
  visibleNodeBounds?: NodeBounds;
  nodeSize?: NodeSize;
}): Point | undefined {
  const point = explicitCanvasPoint ?? lastCanvasPoint ?? viewportCenter;
  if (!point) return undefined;
  if (explicitCanvasPoint) return { ...explicitCanvasPoint };

  if (visibleNodeBounds) {
    const visibleOccupiedBounds = occupiedNodeBounds.filter((occupied) => intersects(occupied, visibleNodeBounds));
    const xCoordinates = visibleCandidateXCoordinates(point, nodeSize, visibleNodeBounds, visibleOccupiedBounds);
    for (let index = 0; index < maximumCandidates; index += 1) {
      const candidate = candidateAt(point, nodeSize, index);
      if (
        isWithin(candidate, visibleNodeBounds) &&
        !visibleOccupiedBounds.some((occupied) => intersects(candidate, occupied))
      ) {
        return { x: candidate.x, y: candidate.y };
      }
    }
    const visibleCandidate = closestVisibleCandidate(
      point,
      nodeSize,
      visibleNodeBounds,
      visibleOccupiedBounds,
      xCoordinates
    );
    if (visibleCandidate) return visibleCandidate;
  }

  let collisionFreeFallback: NodeBounds | undefined;
  for (let index = 0; index < maximumCandidates; index += 1) {
    const candidate = candidateAt(point, nodeSize, index);
    const collisionFree = !occupiedNodeBounds.some((occupied) => intersects(candidate, occupied));
    if (collisionFree && collisionFreeFallback === undefined) {
      collisionFreeFallback = candidate;
    }
  }

  if (collisionFreeFallback) {
    return { x: collisionFreeFallback.x, y: collisionFreeFallback.y };
  }

  return {
    x: occupiedNodeBounds.reduce(
      (rightmost, occupied) => Math.max(rightmost, occupied.x + occupied.width + insertionGap),
      point.x
    ),
    y: point.y
  };
}
